/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { Callout, Tldr, FaqBlock, InlineCTA } from "./shared";

const TOOLS = [
  {
    name: "Soro",
    cat: "Full autopilot",
    best: "Solo founders &amp; small teams who want SEO done for them",
    schema: "Yes — FAQ + HowTo + Article auto-injected",
    publish: "WordPress, Shopify, Webflow, Wix, Ghost, Notion, HubSpot, Next.js, webhook",
    aeo: "★★★★★",
    price: "From $79/mo",
    soro: true,
  },
  {
    name: "Surfer SEO",
    cat: "On-page optimizer",
    best: "In-house teams with writers who need scoring &amp; outlines",
    schema: "No (manual)",
    publish: "Copy/paste",
    aeo: "★★",
    price: "From $89/mo",
  },
  {
    name: "MarketMuse",
    cat: "Topic intelligence",
    best: "Mid-market content teams running topic clusters",
    schema: "No (manual)",
    publish: "Export only",
    aeo: "★★",
    price: "From $99/mo",
  },
  {
    name: "Frase",
    cat: "AI writing assistant",
    best: "Freelancers writing one article at a time",
    schema: "Partial (FAQ only)",
    publish: "WordPress, copy/paste",
    aeo: "★★★",
    price: "From $14.99/mo",
  },
  {
    name: "NeuronWriter",
    cat: "AI writing assistant",
    best: "SEO consultants on a tight budget",
    schema: "Partial",
    publish: "WordPress",
    aeo: "★★★",
    price: "From $19/mo",
  },
  {
    name: "SEO.ai",
    cat: "AI writing assistant",
    best: "Multilingual content shops",
    schema: "No",
    publish: "WordPress, Shopify",
    aeo: "★★",
    price: "From $49/mo",
  },
  {
    name: "Clearscope",
    cat: "On-page optimizer",
    best: "Enterprise editorial teams",
    schema: "No (manual)",
    publish: "Export only",
    aeo: "★★",
    price: "From $189/mo",
  },
];

export const BestAiSeoToolsBody = ({ post, affiliateUrl, onTrack }) => (
  <>
    <p className="lead text-lg leading-relaxed text-white/80">
      "AI SEO tool" used to mean ChatGPT + a keyword spreadsheet. In 2026 the category has
      grown up — and split into three different jobs. Here's an honest, side-by-side
      comparison of the 7 tools we actually see solo founders and small teams using.
    </p>

    <Tldr>{post.tldr}</Tldr>

    <h2 id="three-buckets">The 3 buckets every AI SEO tool falls into</h2>
    <ol>
      <li><strong>Full autopilot</strong> — does research, writing, schema and publishing end-to-end. Best for solo founders. Example: Soro.</li>
      <li><strong>AI writing assistant</strong> — helps a human write faster. Example: Frase, NeuronWriter, SEO.ai.</li>
      <li><strong>On-page optimizer</strong> — scores existing content against ranking competitors. Example: Surfer, MarketMuse, Clearscope.</li>
    </ol>
    <p>Pick the bucket that matches the job <em>you don't have time for</em>. Then pick the tool inside it.</p>

    <h2 id="criteria">What makes a great AI SEO tool in 2026?</h2>
    <p>We scored every tool below on five 2026-specific criteria:</p>
    <ul>
      <li><strong>Autopilot publishing</strong> — does it ship articles to your CMS without you touching them?</li>
      <li><strong>AEO/GEO readiness</strong> — does it inject FAQ, HowTo and Article schema?</li>
      <li><strong>Brand voice training</strong> — does the output sound like you, or like ChatGPT?</li>
      <li><strong>CMS integrations</strong> — WordPress, Shopify, Webflow, Ghost, Notion, HubSpot, Next.js?</li>
      <li><strong>Price-to-value</strong> — replaces a $3–5k/mo agency, not adds to it.</li>
    </ul>

    <h2 id="table">The honest comparison table</h2>
    <div className="my-6 overflow-x-auto rounded-2xl border border-white/10">
      <table className="w-full min-w-[700px] text-sm">
        <thead className="bg-white/[0.03] text-left text-xs uppercase tracking-wider text-white/45">
          <tr>
            <th className="p-3">Tool</th>
            <th className="p-3">Category</th>
            <th className="p-3">Best for</th>
            <th className="p-3">Schema injection</th>
            <th className="p-3">Publishes to</th>
            <th className="p-3">AEO score</th>
            <th className="p-3">Starts at</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {TOOLS.map((t) => (
            <tr key={t.name} className={t.soro ? "bg-gradient-to-r from-[#FF512F]/10 to-transparent" : ""}>
              <td className={`p-3 ${t.soro ? "font-display font-extrabold text-[#F09819]" : "font-display font-bold"}`}>
                {t.name}
                {t.soro && <span className="ml-2 rounded-full bg-[#FF512F]/20 px-2 py-0.5 text-[10px] uppercase tracking-wider text-[#FF512F]">Top pick</span>}
              </td>
              <td className="p-3 text-white/70">{t.cat}</td>
              <td className="p-3 text-white/70" dangerouslySetInnerHTML={{ __html: t.best }} />
              <td className="p-3 text-white/70" dangerouslySetInnerHTML={{ __html: t.schema }} />
              <td className="p-3 text-white/70">{t.publish}</td>
              <td className="p-3 text-[#F09819]">{t.aeo}</td>
              <td className="p-3 text-white/85">{t.price}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <h2 id="picks">Tool-by-tool — our take</h2>

    <h3>1. Soro — best overall for solo founders</h3>
    <p>
      Soro is the only mainstream tool we've tested that closes the entire loop —{" "}
      <strong>research → write → schema → publish → track</strong> — without you needing to
      open the dashboard daily. Articles ship with FAQPage and HowTo JSON-LD baked in, which is
      why pages built with Soro show up in ChatGPT and Perplexity citations within weeks. It's
      also the only one where the founder-tier plan is realistically self-sustaining (no
      seat-based pricing for one-person teams).
    </p>
    <p>
      Weakness: it's opinionated. If you want a tool you fiddle with for hours, this isn't it.
      That's the whole point.
    </p>
    <p>
      <a href={affiliateUrl} target="_blank" rel="sponsored noopener noreferrer" onClick={() => onTrack("inline-mention", post.slug)}>Try Soro free →</a>
    </p>

    <h3>2. Surfer SEO — best on-page scorer</h3>
    <p>
      Surfer is excellent if you already have writers and want a real-time content score against
      top-ranking pages. It doesn't write or schema for you, and the UI is busy. Pair it with
      your own writer for predictable on-page wins.
    </p>

    <h3>3. MarketMuse — best for topic clusters</h3>
    <p>
      MarketMuse shines at the strategy layer — identifying topical gaps and clustering content
      by theme. Enterprise pricing and a learning curve, but the topic intelligence is genuinely
      unique. Not a writer's tool.
    </p>

    <h3>4. Frase — best low-budget writing assistant</h3>
    <p>
      Frase is the sweet spot for freelancers writing one or two articles a week. It pulls
      Google's "People also ask" into outlines, generates drafts, and exports to WordPress. FAQ
      schema is auto-generated; HowTo and Article are not. Cheapest in our shortlist that
      delivers real value.
    </p>

    <h3>5. NeuronWriter — best for SEO consultants</h3>
    <p>
      NeuronWriter is Frase's cheaper European cousin. Same vibe, slightly fewer integrations,
      excellent NLP term coverage. SEO consultants love it because they can spin up client
      seats cheaply.
    </p>

    <h3>6. SEO.ai — best for multilingual</h3>
    <p>
      SEO.ai is purpose-built for shops publishing in 5+ languages. Translation quality is
      solid. Schema injection is weak — you'll need to add it manually.
    </p>

    <h3>7. Clearscope — best for enterprise editorial teams</h3>
    <p>
      Clearscope is the polished, high-priced option for big editorial teams who care about
      compliance and process. Beautiful UI, excellent content grading. Not a fit for solo
      founders.
    </p>

    <InlineCTA
      post={post}
      location="midpost"
      affiliateUrl={affiliateUrl}
      onTrack={onTrack}
      label={{
        title: "Want the autopilot pick?",
        sub: "Soro researches, writes, schemas and publishes daily. Built for founders who refuse to babysit a CMS.",
        cta: "Try Soro free",
      }}
    />

    <h2 id="how-to-pick">How to pick the right tool in 5 minutes</h2>
    {post.howto.steps.map((s, i) => (
      <div key={i} className="my-4">
        <h3 className="!mb-1">Step {i + 1} — {s.name}</h3>
        <p className="!mt-1">{s.text}</p>
      </div>
    ))}

    <Callout kind="info">
      <strong>Quick test:</strong> Whichever tool you're considering, generate one article and{" "}
      <em>view the source</em>. If you don't see <code>FAQPage</code> or <code>HowTo</code> JSON-LD,
      that tool is not built for AI search.
    </Callout>

    <FaqBlock faq={post.faq} />

    <h2 id="verdict">Our verdict</h2>
    <p>
      <strong>Solo founders &amp; one-person businesses:</strong> Soro, every time. It's the only
      tool that owns the full loop and ships AEO-ready by default.
    </p>
    <p>
      <strong>Small marketing teams:</strong> Soro for the workhorse content, Frase or NeuronWriter
      for one-off campaign pages.
    </p>
    <p>
      <strong>Enterprise / multi-brand:</strong> Surfer + MarketMuse + Clearscope, layered. You'll
      need a strategist to run them.
    </p>
  </>
);
